import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';

interface Session { id: string; date: string; status: string; attendance_status: string; }
interface PortalClass { id: string; name: string; day_of_week: string; start_time: string; end_time: string; teacher_name: string | null; dance_type_name: string | null; level_name: string | null; sessions: Session[]; }

/** Student portal — enrolled classes with per-session attendance toggles. */
@customElement('cadence-portal-classes')
export class CadencePortalClasses extends LitElement {
  @state() private classes: PortalClass[] = [];
  @state() private loading = true;
  @state() private busy = '';

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.6rem; }
    .class-head { display: flex; flex-direction: column; gap: 2px; }
    .class-head .meta { color: var(--color-text-secondary); font-size: 0.85rem; }
    .sessions { display: flex; flex-direction: column; gap: 8px; margin-top: var(--space-3); }
    .session { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); }
    .session.cancelled { opacity: 0.6; }
    .date { font-weight: 600; }
    .controls { display: flex; gap: 8px; align-items: center; }
    .toggle { border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); border-radius: var(--radius-md); padding: 6px 12px; cursor: pointer; font: inherit; font-size: 0.82rem; }
    .toggle.on-yes { background: var(--color-success); color: #fff; border-color: transparent; }
    .toggle.on-no { background: var(--color-text-secondary); color: #fff; border-color: transparent; }
    .absent { color: var(--color-danger); font-size: 0.8rem; font-weight: 600; }
    .stack { display: grid; gap: var(--space-4); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try { this.classes = await api.get<PortalClass[]>('/portal/classes'); }
    finally { this.loading = false; }
  }

  private async mark(session: Session, status: 'attending' | 'not_attending') {
    this.busy = session.id;
    try {
      await api.post(`/portal/attendance/${session.id}`, { status });
      session.attendance_status = status;
      this.classes = [...this.classes];
      showToast(status === 'attending' ? t('common.portal.attending') : t('common.portal.notAttending'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.busy = ''; }
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <h1>${t('common.portalNav.classes')}</h1>
      ${this.classes.length
        ? html`<div class="stack">${this.classes.map((c) => this.renderClass(c))}</div>`
        : html`<cadence-empty-state heading=${t('common.portal.noClasses')}></cadence-empty-state>`}
    `;
  }

  private renderClass(c: PortalClass) {
    return html`
      <cadence-card>
        <div class="class-head">
          <strong>${c.name}</strong>
          <span class="meta">${[c.dance_type_name, c.level_name].filter(Boolean).join(' · ')}</span>
          <span class="meta">${c.day_of_week ?? ''} ${c.start_time ?? ''}–${c.end_time ?? ''} ${c.teacher_name ? '· ' + c.teacher_name : ''}</span>
        </div>
        <div class="sessions">
          ${(c.sessions ?? []).map((s) => this.renderSession(s))}
        </div>
      </cadence-card>
    `;
  }

  private renderSession(s: Session) {
    if (s.status === 'cancelled') {
      return html`<div class="session cancelled"><span class="date">${s.date}</span><cadence-badge color="var(--color-danger)">${t('portal.common.cancelled')}</cadence-badge></div>`;
    }
    const yes = s.attendance_status === 'attending';
    const no = s.attendance_status === 'not_attending';
    return html`
      <div class="session">
        <span class="date">${s.date}</span>
        <div class="controls">
          ${s.attendance_status === 'absent' ? html`<span class="absent">${t('common.portal.absent')}</span>` : ''}
          <button class="toggle ${yes ? 'on-yes' : ''}" ?disabled=${this.busy === s.id} @click=${() => this.mark(s, 'attending')}>
            ${t('common.portal.willAttend')}
          </button>
          <button class="toggle ${no ? 'on-no' : ''}" ?disabled=${this.busy === s.id} @click=${() => this.mark(s, 'not_attending')}>
            ${t('common.portal.wontAttend')}
          </button>
        </div>
      </div>
    `;
  }
}
